/**
 * @fileoverview Algoritmo de Centroide Huber para coordenadas
 * @module types/errors/huberCentroid
 * @version 1.0.0
 *
 * Implementa el estimador M de Huber usando IRLS (Iteratively Reweighted Least Squares)
 * para calcular un centroide robusto que minimiza el impacto de outliers.
 *
 * Contexto PTEL: Cuando múltiples fuentes de geocodificación devuelven coordenadas
 * diferentes para la misma infraestructura, Huber permite obtener un "consenso"
 * que no se ve arrastrado por una fuente con datos incorrectos.
 *
 * @example
 * ```typescript
 * const sources = [
 *   { x: 437250, y: 4136870, weight: 0.9 },  // WFS oficial
 *   { x: 437248, y: 4136865, weight: 0.85 }, // CartoCiudad
 *   { x: 437320, y: 4137100, weight: 0.6 },  // Nominatim (outlier)
 * ];
 *
 * const centroid = huberCentroid(sources);
 * // Resultado: ~{ x: 437249, y: 4136867 } (Nominatim casi ignorado)
 * ```
 */

// =====================================================
// TIPOS
// =====================================================

export interface WeightedPoint {
  /** Coordenada X (UTM) */
  x: number;
  /** Coordenada Y (UTM) */
  y: number;
  /** Peso inicial (0-1), típicamente la confianza de la fuente */
  weight?: number;
  /** Identificador de la fuente (para debugging) */
  source?: string;
}

export interface HuberResult {
  /** Centroide calculado */
  centroid: { x: number; y: number };
  /** Número de iteraciones hasta convergencia */
  iterations: number;
  /** ¿Convergió dentro del límite de iteraciones? */
  converged: boolean;
  /** Pesos finales de cada punto (orden igual al input) */
  finalWeights: number[];
  /** Puntos identificados como outliers (peso final < 0.3) */
  outlierIndices: number[];
  /** Error residual final */
  residualError: number;
  /** Estadísticas del proceso */
  stats: {
    /** Distancia media al centroide */
    meanDistance: number;
    /** Distancia máxima al centroide */
    maxDistance: number;
    /** Desviación estándar de distancias */
    stdDistance: number;
  };
}

export interface HuberOptions {
  /**
   * Umbral de transición entre pérdida cuadrática y lineal (en metros)
   * Puntos a distancia < threshold tienen influencia cuadrática (normal)
   * Puntos a distancia >= threshold tienen influencia lineal (reducida)
   * Default: 50 metros
   */
  threshold?: number;
  /**
   * Máximo número de iteraciones IRLS
   * Default: 15
   */
  maxIterations?: number;
  /**
   * Tolerancia para convergencia (metros)
   * Si el centroide se mueve menos que esto, se considera convergido
   * Default: 0.1 metros
   */
  tolerance?: number;
  /**
   * Peso mínimo para evitar división por cero
   * Default: 0.01
   */
  minWeight?: number;
}

// =====================================================
// CONSTANTES
// =====================================================

const DEFAULT_OPTIONS: Required<HuberOptions> = {
  threshold: 50,      // 50 metros - puntos más lejos tienen influencia reducida
  maxIterations: 15,  // Suficiente para convergencia típica
  tolerance: 0.1,     // 10 cm de precisión
  minWeight: 0.01,    // Evita división por cero
};

// =====================================================
// FUNCIONES AUXILIARES
// =====================================================

/**
 * Calcula distancia euclidiana entre dos puntos
 */
function distance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calcula la mediana de un array de números
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Calcula mediana geométrica (punto de partida robusto)
 * Usa aproximación iterativa de Weiszfeld
 */
function geometricMedian(
  points: WeightedPoint[],
  maxIter = 10
): { x: number; y: number } {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  if (points.length === 1) {
    return { x: points[0].x, y: points[0].y };
  }

  // Inicio con mediana por componentes
  let centroid = {
    x: median(points.map(p => p.x)),
    y: median(points.map(p => p.y)),
  };

  for (let i = 0; i < maxIter; i++) {
    let sumX = 0;
    let sumY = 0;
    let sumWeights = 0;

    for (const point of points) {
      const dist = distance(point, centroid);
      if (dist < 0.001) continue; // Evitar división por cero

      const w = (point.weight ?? 1) / dist;
      sumX += w * point.x;
      sumY += w * point.y;
      sumWeights += w;
    }

    if (sumWeights < 0.001) break;

    const newCentroid = {
      x: sumX / sumWeights,
      y: sumY / sumWeights,
    };

    // Verificar convergencia
    if (distance(centroid, newCentroid) < 0.01) break;
    centroid = newCentroid;
  }

  return centroid;
}

/**
 * Calcula peso Huber para una distancia dada
 * w(d) = 1 si d < threshold
 * w(d) = threshold / d si d >= threshold
 */
function huberWeight(dist: number, threshold: number, minWeight: number): number {
  if (dist < threshold) {
    return 1;
  }
  return Math.max(minWeight, threshold / dist);
}

/**
 * Calcula media ponderada
 */
function weightedMean(
  points: WeightedPoint[],
  weights: number[]
): { x: number; y: number } {
  let sumX = 0;
  let sumY = 0;
  let sumW = 0;

  for (let i = 0; i < points.length; i++) {
    const w = weights[i] * (points[i].weight ?? 1);
    sumX += w * points[i].x;
    sumY += w * points[i].y;
    sumW += w;
  }

  if (sumW < 0.0001) {
    // Fallback a media simple si todos los pesos son cero
    return {
      x: points.reduce((s, p) => s + p.x, 0) / points.length,
      y: points.reduce((s, p) => s + p.y, 0) / points.length,
    };
  }

  return {
    x: sumX / sumW,
    y: sumY / sumW,
  };
}

/**
 * Calcula estadísticas de distancias
 */
function calculateStats(
  points: WeightedPoint[],
  centroid: { x: number; y: number }
): HuberResult['stats'] {
  const distances = points.map(p => distance(p, centroid));

  const meanDistance = distances.reduce((s, d) => s + d, 0) / distances.length;
  const maxDistance = Math.max(...distances);

  const variance = distances.reduce((s, d) => s + (d - meanDistance) ** 2, 0) / distances.length;
  const stdDistance = Math.sqrt(variance);

  return { meanDistance, maxDistance, stdDistance };
}

// =====================================================
// FUNCIÓN PRINCIPAL
// =====================================================

/**
 * Calcula el centroide Huber de un conjunto de puntos ponderados
 *
 * El algoritmo usa IRLS (Iteratively Reweighted Least Squares):
 * 1. Inicia con la mediana geométrica (robusta)
 * 2. En cada iteración:
 *    - Calcula distancia de cada punto al centroide actual
 *    - Asigna peso Huber: 1 si cerca, threshold/distancia si lejos
 *    - Calcula nuevo centroide como media ponderada
 * 3. Repite hasta convergencia o máximo de iteraciones
 *
 * @param points - Puntos con coordenadas y pesos opcionales
 * @param options - Opciones de configuración
 * @returns Resultado con centroide y métricas
 */
export function huberCentroid(
  points: WeightedPoint[],
  options?: HuberOptions
): HuberResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Casos especiales
  if (points.length === 0) {
    return {
      centroid: { x: 0, y: 0 },
      iterations: 0,
      converged: true,
      finalWeights: [],
      outlierIndices: [],
      residualError: 0,
      stats: { meanDistance: 0, maxDistance: 0, stdDistance: 0 },
    };
  }

  if (points.length === 1) {
    return {
      centroid: { x: points[0].x, y: points[0].y },
      iterations: 0,
      converged: true,
      finalWeights: [1],
      outlierIndices: [],
      residualError: 0,
      stats: { meanDistance: 0, maxDistance: 0, stdDistance: 0 },
    };
  }

  if (points.length === 2) {
    // Con 2 puntos, usar media ponderada simple
    const w1 = points[0].weight ?? 1;
    const w2 = points[1].weight ?? 1;
    const totalW = w1 + w2;
    const centroid = {
      x: (points[0].x * w1 + points[1].x * w2) / totalW,
      y: (points[0].y * w1 + points[1].y * w2) / totalW,
    };
    return {
      centroid,
      iterations: 1,
      converged: true,
      finalWeights: [w1 / totalW, w2 / totalW],
      outlierIndices: [],
      residualError: distance(points[0], points[1]) / 2,
      stats: calculateStats(points, centroid),
    };
  }

  // Inicio: mediana geométrica (robusta a outliers)
  let centroid = geometricMedian(points);
  let weights = points.map(() => 1);
  let converged = false;
  let iterations: number;

  // IRLS loop
  for (iterations = 1; iterations <= opts.maxIterations; iterations++) {
    // Calcular distancias y pesos Huber
    const distances = points.map(p => distance(p, centroid));
    weights = distances.map(d => huberWeight(d, opts.threshold, opts.minWeight));

    // Calcular nuevo centroide ponderado
    const newCentroid = weightedMean(points, weights);

    // Verificar convergencia
    const movement = distance(centroid, newCentroid);
    if (movement < opts.tolerance) {
      converged = true;
      centroid = newCentroid;
      break;
    }

    centroid = newCentroid;
  }

  // Calcular resultados finales
  const finalDistances = points.map(p => distance(p, centroid));
  const outlierIndices = weights
    .map((w, i) => (w < 0.3 ? i : -1))
    .filter(i => i >= 0);

  const residualError = finalDistances.reduce(
    (sum, d, i) => sum + weights[i] * d * d,
    0
  ) / points.length;

  return {
    centroid,
    iterations,
    converged,
    finalWeights: weights,
    outlierIndices,
    residualError: Math.sqrt(residualError),
    stats: calculateStats(points, centroid),
  };
}

// =====================================================
// FUNCIONES DE CONVENIENCIA
// =====================================================

/**
 * Versión simplificada que solo devuelve el centroide
 */
export function huberCentroidSimple(
  points: Array<{ x: number; y: number }>,
  threshold = 50
): { x: number; y: number } {
  const result = huberCentroid(
    points.map(p => ({ x: p.x, y: p.y })),
    { threshold }
  );
  return result.centroid;
}

/**
 * Calcula centroide Huber desde resultados de geocodificación
 */
export function huberCentroidFromSources(
  sources: Array<{
    source: string;
    coordinates: { x: number; y: number };
    confidence: number;
  }>,
  options?: HuberOptions
): HuberResult & { sourceWeights: Record<string, number> } {
  const points: WeightedPoint[] = sources.map(s => ({
    x: s.coordinates.x,
    y: s.coordinates.y,
    weight: s.confidence / 100, // Normalizar confianza a 0-1
    source: s.source,
  }));

  const result = huberCentroid(points, options);

  // Crear mapa de pesos por fuente
  const sourceWeights: Record<string, number> = {};
  sources.forEach((s, i) => {
    sourceWeights[s.source] = result.finalWeights[i];
  });

  return { ...result, sourceWeights };
}

/**
 * Detecta si hay un outlier claro en el conjunto de puntos
 */
export function detectOutliers(
  points: WeightedPoint[],
  threshold = 50
): {
  hasOutliers: boolean;
  outlierIndices: number[];
  outlierSources: string[];
  cleanPoints: WeightedPoint[];
} {
  const result = huberCentroid(points, { threshold });

  const outlierSources = result.outlierIndices
    .map(i => points[i].source)
    .filter((s): s is string => s !== undefined);

  const cleanPoints = points.filter((_, i) => !result.outlierIndices.includes(i));

  return {
    hasOutliers: result.outlierIndices.length > 0,
    outlierIndices: result.outlierIndices,
    outlierSources,
    cleanPoints,
  };
}

/**
 * Calcula centroide simple (media aritmética) para comparación
 */
export function simpleCentroid(
  points: Array<{ x: number; y: number; weight?: number }>
): { x: number; y: number } {
  if (points.length === 0) return { x: 0, y: 0 };

  let sumX = 0;
  let sumY = 0;
  let sumW = 0;

  for (const p of points) {
    const w = p.weight ?? 1;
    sumX += p.x * w;
    sumY += p.y * w;
    sumW += w;
  }

  return {
    x: sumX / sumW,
    y: sumY / sumW,
  };
}

/**
 * Compara centroide Huber vs simple y devuelve la diferencia
 */
export function compareCentroids(
  points: WeightedPoint[],
  threshold = 50
): {
  huber: { x: number; y: number };
  simple: { x: number; y: number };
  difference: number;
  huberBetter: boolean;
} {
  const huberResult = huberCentroid(points, { threshold });
  const simpleResult = simpleCentroid(points);
  const diff = distance(huberResult.centroid, simpleResult);

  // Huber es "mejor" si hay outliers detectados
  const huberBetter = huberResult.outlierIndices.length > 0;

  return {
    huber: huberResult.centroid,
    simple: simpleResult,
    difference: diff,
    huberBetter,
  };
}
